/**
 * The Producer bench — Room 1 (`docs/ROOMS.md` §5, Build Plan 01 P1).
 *
 * A room, not a toolbox. Upload, cut-review, library and listing were four
 * unconnected routes; the bench is the one surface that shows a producer where
 * they stand and hands them their next move.
 *
 * Posture (§1): the producer is the game, not a spectator. Every panel is a place
 * you act from. Copy is first-person and imperative — no questions, no "would you
 * like to". The one station you cannot act on (the render) says plainly that the
 * bench is working and shows its work; it never fakes progress.
 *
 * The four stations map onto real state, not decoration:
 *
 *   DIG   bring in raw sound            → always open
 *   CHOP  asset `awaiting_review`       → YOUR move: confirm the cuts
 *   PACK  asset `pending`/`processing`  → the bench's move: the render is running
 *   SHIP  asset `ready`                 → YOUR move: list it
 *
 * Recovering a chop is why `GET /v1/me/jobs` exists. `/review/:jobId` needs a job
 * id that used to live only in the uploader's React state, so a producer who
 * closed the tab stranded the asset forever. Note the cut-review job is `complete`
 * (not `awaiting_user_input` — that state is only reached *after* cut points are
 * submitted), so the bench matches assets to jobs rather than filtering by state.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { IconUpload, IconWave, IconGrid, IconStore, IconArrowRight, IconWarn } from '../components/ui/icons';
import { assets as assetsApi, jobs as jobsApi } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { track } from '../lib/analytics';
import type { AssetSummary, JobSummary } from '../lib/types';

/** Which station a station is: can you act, is the bench working, or is it quiet? */
type StationState = 'act' | 'working' | 'idle';

interface Station {
  key: 'dig' | 'chop' | 'pack' | 'ship';
  label: string;
  verb: string;
  count: number;
  state: StationState;
}

/** The asset the producer should act on next, and the move it wants. */
interface NextMove {
  kind: 'chop' | 'ship' | 'dig' | 'wait';
  asset?: AssetSummary;
  jobId?: string;
  pending?: number;
}

/**
 * Match each asset to the job that produced it — newest first, preferring the
 * slice job. The API returns jobs newest-first, so the first match wins.
 */
function jobForAsset(jobs: readonly JobSummary[], assetId: string): JobSummary | undefined {
  return jobs.find((j) => j.asset_id === assetId && j.kind === 'slice') ??
    jobs.find((j) => j.asset_id === assetId);
}

/** Derive the room's state from what the producer actually has. */
export function deriveBench(assets: readonly AssetSummary[], jobs: readonly JobSummary[]) {
  const chop = assets.filter((a) => a.processing_state === 'awaiting_review');
  const pack = assets.filter(
    (a) => a.processing_state === 'pending' || a.processing_state === 'processing',
  );
  const ship = assets.filter((a) => a.processing_state === 'ready');
  const failed = assets.filter((a) => a.processing_state === 'failed');

  // A chop is only recoverable if we can find the job id /review/:jobId needs.
  const resumable = chop
    .map((a) => ({ asset: a, job: jobForAsset(jobs, a.id) }))
    .filter((x): x is { asset: AssetSummary; job: JobSummary } => Boolean(x.job));

  // Your move first, always. The bench working is not a move.
  let next: NextMove;
  if (resumable.length > 0 && resumable[0]) {
    next = { kind: 'chop', asset: resumable[0].asset, jobId: resumable[0].job.id };
  } else if (ship.length > 0 && ship[0]) {
    next = { kind: 'ship', asset: ship[0] };
  } else if (pack.length > 0) {
    next = { kind: 'wait', pending: pack.length };
  } else {
    next = { kind: 'dig' };
  }

  return { chop, pack, ship, failed, resumable, next };
}

function StationCard({ station }: { station: Station }) {
  const Icon = { dig: IconUpload, chop: IconWave, pack: IconGrid, ship: IconStore }[station.key];
  const tone =
    station.state === 'act'
      ? 'border-accent text-primary'
      : station.state === 'working'
        ? 'border-border-strong text-primary'
        : 'border-border-subtle text-secondary';

  return (
    <Card className={`flex items-center gap-3 border p-4 ${tone}`}>
      <Icon size={20} className={station.state === 'act' ? 'text-accent' : 'text-muted'} aria-hidden />
      <div className="min-w-0 grow">
        <div className="type-label text-secondary">{station.label}</div>
        <div className="type-heading-2 text-primary">{station.verb}</div>
      </div>
      <div className="flex items-center gap-2">
        {station.state === 'working' ? (
          // The one honest "live" signal in the flow — the retired lime, rescoped.
          // Square, not a dot: this theme replaces borderRadius (max 4px, no
          // circles), so `rounded-full` would be a dead class.
          <span
            className="inline-block h-2 w-2 rounded-sm bg-live motion-safe:animate-pulse"
            aria-hidden
          />
        ) : null}
        <span className="type-data tabular-nums text-primary">{station.count}</span>
      </div>
    </Card>
  );
}

export function Bench() {
  const { t } = useI18n();
  const [assets, setAssets] = useState<AssetSummary[] | null>(null);
  const [jobs, setJobs] = useState<JobSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;

    Promise.all([assetsApi.mine({ limit: 50 }), jobsApi.mine({ limit: 50 }, ac.signal)])
      .then(([a, j]) => {
        if (cancelled) return;
        setAssets(a.items);
        setJobs(j.items);
      })
      .catch((e: unknown) => {
        if (cancelled || ac.signal.aborted) return;
        setError(apiErrorMessage(e, t));
      });

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [t]);

  const loading = assets === null || jobs === null;
  const bench = useMemo(() => deriveBench(assets ?? [], jobs ?? []), [assets, jobs]);

  // The funnel's first step: entering the room, tagged with the move the
  // producer arrived to (`next.kind`) and how much work is standing where. Fires
  // once per visit, only after the data settles — never on the loading pass.
  const entered = useRef(false);
  useEffect(() => {
    if (loading || entered.current) return;
    entered.current = true;
    track('bench_entered', {
      next_move: bench.next.kind,
      chop: bench.chop.length,
      pack: bench.pack.length,
      ship: bench.ship.length,
    });
  }, [loading, bench]);

  const stations: Station[] = [
    { key: 'dig', label: t.bench.dig, verb: t.bench.digVerb, count: assets?.length ?? 0, state: 'act' },
    {
      key: 'chop',
      label: t.bench.chop,
      verb: t.bench.chopVerb,
      count: bench.chop.length,
      state: bench.resumable.length > 0 ? 'act' : 'idle',
    },
    {
      key: 'pack',
      label: t.bench.pack,
      verb: t.bench.packVerb,
      count: bench.pack.length,
      state: bench.pack.length > 0 ? 'working' : 'idle',
    },
    {
      key: 'ship',
      label: t.bench.ship,
      verb: t.bench.shipVerb,
      count: bench.ship.length,
      state: bench.ship.length > 0 ? 'act' : 'idle',
    },
  ];

  return (
    <>
      <PageHeader title={t.bench.title} subtitle={t.bench.subtitle} />

      {error ? (
        <Card className="mb-6 flex items-center gap-3 border border-danger p-4">
          <IconWarn size={20} className="text-danger" aria-hidden />
          <p className="type-body text-primary">{error}</p>
        </Card>
      ) : null}

      {/* The move. One primary action per view — the thing to do next. */}
      <Card className="mb-6 border border-border-strong p-5 sm:p-6">
        {loading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-11 w-40" />
          </div>
        ) : (
          <>
            <p className="type-label text-accent">{t.bench.yourMove}</p>
            <h2 className="type-display-lg mt-2 text-primary">{t.bench.move[bench.next.kind].title}</h2>
            <p className="type-body text-secondary mt-2 max-w-prose">
              {bench.next.kind === 'wait'
                ? t.bench.move.wait.body(bench.next.pending ?? 0)
                : bench.next.kind === 'dig'
                  ? t.bench.move.dig.body
                  : t.bench.move[bench.next.kind].body(bench.next.asset?.title ?? '')}
            </p>

            <div className="mt-5">
              {bench.next.kind === 'chop' && bench.next.jobId ? (
                <Link to={`/review/${bench.next.jobId}`}>
                  <Button variant="primary" size="lg" iconRight={<IconArrowRight size={18} />}>
                    {t.bench.move.chop.cta}
                  </Button>
                </Link>
              ) : bench.next.kind === 'ship' && bench.next.asset ? (
                <Link to={`/listings/new?asset=${bench.next.asset.id}`}>
                  <Button variant="primary" size="lg" iconRight={<IconArrowRight size={18} />}>
                    {t.bench.move.ship.cta}
                  </Button>
                </Link>
              ) : bench.next.kind === 'dig' ? (
                <Link to="/studio">
                  <Button variant="primary" size="lg" iconRight={<IconArrowRight size={18} />}>
                    {t.bench.move.dig.cta}
                  </Button>
                </Link>
              ) : (
                // The bench is working. Show its work; never fake a move.
                <p className="type-data text-muted">{t.bench.move.wait.cta}</p>
              )}
            </div>
          </>
        )}
      </Card>

      {/* The stations — where the work stands. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {loading
          ? [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[76px]" />)
          : stations.map((s) => <StationCard key={s.key} station={s} />)}
      </div>

      {/* Anything the bench could not finish. Named, with a way out. */}
      {!loading && bench.failed.length > 0 ? (
        <Card className="mt-6 border border-danger p-4">
          <div className="flex items-center gap-3">
            <IconWarn size={20} className="text-danger" aria-hidden />
            <p className="type-body text-primary grow">{t.bench.failed(bench.failed.length)}</p>
            <Link to="/library">
              <Button variant="ghost" size="sm">
                {t.bench.failedCta}
              </Button>
            </Link>
          </div>
        </Card>
      ) : null}

      {/* Chops the bench cannot resume: no job row to point /review/:jobId at. */}
      {!loading && bench.chop.length > bench.resumable.length ? (
        <p className="type-caption text-muted mt-4">
          {t.bench.unresumable(bench.chop.length - bench.resumable.length)}
        </p>
      ) : null}
    </>
  );
}
